// ==========================
// Version 1 - auth/AuthActionHandler.js
// - Handles Firebase email action links inside BWK
// - Applies email verification action codes and refreshes current auth state
// ==========================
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import Seo from '../components/Seo';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/toast';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';
const TEAL = '#0F766E';

function AuthActionHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCurrentUser } = useAuth();
  const [state, setState] = React.useState({
    status: 'working',
    title: 'Verifying your email',
    message: 'One moment while we confirm your email address.',
  });

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const pageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/auth/action`
    : undefined;

  React.useEffect(() => {
    let cancelled = false;

    async function runAction() {
      if (mode !== 'verifyEmail' || !oobCode) {
        setState({
          status: 'error',
          title: 'Verification link not recognized',
          message: 'Please request a fresh verification email and try again.',
        });
        return;
      }

      try {
        await applyActionCode(auth, oobCode);
        if (auth.currentUser) {
          await refreshCurrentUser();
          await auth.currentUser?.getIdToken(true);
        }

        if (cancelled) return;
        setState({
          status: 'success',
          title: 'Email verified',
          message: 'Your email is confirmed. You can continue to your memories.',
        });
        toast.success('Email verified.');
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          title: 'Try verifying your email again',
          message:
            err?.code === 'auth/invalid-action-code'
              ? 'This verification link has expired or has already been used. Please request a new one.'
              : err?.message || 'We could not verify this email link.',
        });
      }
    }

    runAction();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, refreshCurrentUser]);

  const isSuccess = state.status === 'success';
  const isWorking = state.status === 'working';

  return (
    <div style={styles.page}>
      <Seo
        title="Email verification • Blessed with a Kiss"
        description="Complete your BWK email verification."
        url={pageUrl}
        type="website"
      />

      <div style={styles.glowTL} />
      <div style={styles.glowBR} />

      <section style={styles.card} aria-label="Email verification result">
        <h2 style={styles.title}>{state.title}</h2>
        <p style={styles.subtitle}>{state.message}</p>

        <div style={isSuccess ? styles.success : isWorking ? styles.info : styles.error}>
          {isWorking ? 'Checking link...' : isSuccess ? 'Verification complete.' : 'Verification failed.'}
        </div>

        <div style={styles.actions}>
          {isSuccess ? (
            <button type="button" style={styles.primaryBtn} onClick={() => navigate('/home', { replace: true })}>
              Continue
            </button>
          ) : (
            <Link to="/verify-email" style={styles.primaryLink}>
              Request a new email
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100dvh - 64px)',
    background: NAVY,
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  glowTL: {
    position: 'absolute',
    top: -120,
    left: -120,
    width: 280,
    height: 280,
    borderRadius: '50%',
    filter: 'blur(40px)',
    opacity: 0.25,
    background: GOLD,
  },
  glowBR: {
    position: 'absolute',
    bottom: -140,
    right: -140,
    width: 340,
    height: 340,
    borderRadius: '50%',
    filter: 'blur(50px)',
    opacity: 0.2,
    background: GOLD,
  },
  card: {
    width: 'min(92vw, 440px)',
    background: IVORY,
    borderRadius: 20,
    padding: 22,
    boxShadow: '0 24px 64px rgba(10,26,51,0.40)',
    border: `1px solid rgba(201,164,76,0.35)`,
    color: NAVY,
  },
  title: {
    margin: 0,
    fontFamily: 'serif',
    fontSize: 26,
  },
  subtitle: {
    margin: '8px 0 16px',
    color: WARM_GRAY,
    lineHeight: 1.5,
  },
  info: {
    margin: '8px 0 12px',
    background: '#F8F4E8',
    color: NAVY,
    border: '1px solid rgba(201,164,76,0.45)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  success: {
    margin: '8px 0 12px',
    background: 'rgba(15,118,110,0.10)',
    color: TEAL,
    border: '1px solid rgba(15,118,110,0.32)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  error: {
    margin: '8px 0 12px',
    background: 'rgba(92,26,43,0.08)',
    color: BURGUNDY,
    border: '1px solid rgba(92,26,43,0.28)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  actions: {
    display: 'grid',
    gap: 10,
    marginTop: 14,
  },
  primaryBtn: {
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
  },
  primaryLink: {
    display: 'block',
    textAlign: 'center',
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
    textDecoration: 'none',
  },
};

export default AuthActionHandler;

// ==========================
// End Version 1 - auth/AuthActionHandler.js
// ==========================
